<script setup>
import {computed, defineProps} from 'vue'

const props = defineProps({
  card: {type: Object, required: true},
})

// content fields (equivalent to data.header / data.subheader / data.bodytext)
const headline = computed(() => props.card?.header || '')
const subheadline = computed(() => props.card?.subheader || '')
const body = computed(() => props.card?.bodytext || '')

// settings come from FlexForm: props.card.settings
const settings = computed(() => props.card?.settings || {})

// tags
const headerTag = computed(() => settings.value.headerType || 'h2')
const subheaderTag = computed(() => settings.value.subheaderType || 'p')

// style-related settings
const headlineColor = computed(() => settings.value.headlineColor || '')
const subheadlineColor = computed(() => settings.value.subheadlineColor || '')
const decreaseWidth = computed(() => !!settings.value.decreaseWidth)
const listStyle = computed(() => settings.value.listStyle || '')
const headerLookalike = computed(() => settings.value.headerLookalike || '')
const subheaderLookalike = computed(() => settings.value.subheaderLookalike || '')

// visibility
const hideForABTest = computed(() => !!settings.value.hideForABTest)
const hideMobile = computed(() => !!settings.value.hideMobile)
const hideTablet = computed(() => !!settings.value.hideTablet)
const hideDesktop = computed(() => !!settings.value.hideDesktop)

// anchor
const anchor = computed(() => settings.value.anchor || '')

// wrapper classes (Fluid: big class soup on <div ...>)
const wrapperClasses = computed(() => {
  const classes = [
    'content-item',
    'module-headline-paragraph',
    'headline',
    'paragraph',
  ]

  if (headlineColor.value) {
    classes.push(headlineColor.value)
  }

  if (subheadlineColor.value) {
    classes.push(subheadlineColor.value)
  }

  if (decreaseWidth.value) {
    classes.push('paragraph--decreased-width')
  }

  if (listStyle.value) {
    classes.push(listStyle.value)
  }

  if (hideForABTest.value) {
    classes.push('-hide-ab-test')
  }
  if (hideMobile.value) {
    classes.push('-hide-mobile')
  }
  if (hideTablet.value) {
    classes.push('-hide-tablet')
  }
  if (hideDesktop.value) {
    classes.push('-hide-desktop')
  }

  return classes
})

// headline <hX> classes: "hl" + optional lookalike
const headlineClasses = computed(() => {
  const classes = ['hl']
  if (headerLookalike.value) {
    classes.push(headerLookalike.value)
  }
  return classes
})

// subheadline classes: "sl" + maybe "heading" + lookalike
const subheadlineClasses = computed(() => {
  const classes = ['sl']
  if (subheaderTag.value === 'span') {
    classes.push('heading')
  }
  if (subheaderLookalike.value) {
    classes.push(subheaderLookalike.value)
  }
  return classes
})
</script>

<template>
  <div
    :class="wrapperClasses"
    :id="anchor || null"
  >
    <component
      v-if="headline"
      :is="headerTag"
      :class="headlineClasses"
    >
      {{ headline }}
    </component>
    <component
      v-if="subheadline"
      :is="subheaderTag"
      :class="subheadlineClasses"
    >
      {{ subheadline }}
    </component>
    <div v-if="body" v-html="body"></div>
  </div>
</template>
